/**
 * @namespace Video
 * @memberof Promega.Components
 * @description Methods for controlling video components. Most video components on the site are represented by a wrapper that contains details
 * about the video within <code>data-</code> attributes, and a placeholder image as an <code>&lt;img&gt;</code> element within the wrapper.<br><br>
 * This component makes use of the [HTML5 History API]{@link https://developer.mozilla.org/en-US/docs/Web/API/History}. Triggering a modal video adds an entry to your browser history,
 * and dismissing the modal video pops the most recent entry from your browser history. Therefore, when a modal video is triggered, pressing the "back" button in your browser will cause
 * the video to be dismissed, and then pressing the "forward" button in your browser will trigger the video modal again. This is particularly useful on
 * mobile devices that include a "back" button as part of their default hardware or software UI; for example, if you trigger a modal video on a Samsung Galaxy phone you can
 * dismiss it by pressing your device's system "back" button.
 * 
 * @example <caption>An element is considered a wrapper if it has one of the following trigger classes in CSS: <code>trigger--modal-video</code> or <code>trigger--inline-video</code>.<br>
 * The video's source URL must be provided via the <code>data-video-src</code> attribute. Any element may be a video trigger, although it is most commonly used to wrap an image as a poster.</caption>
 * <div class="trigger--modal-video"
 *   data-video-src="path-to-the-video.mp4"></div>
 *   
 * @example <caption>Additional details may be provided via additional <code>data-</code> attributes. When the video component is triggered by a click, the
 * <code>data-video-poster</code> attribute will be used to show a poster image for the replacement <code>&lt;video&gt;</code> element on mobile
 * devices that do not support programmatic autoplay.</caption>
 * <div class="trigger--modal-video"
 *   data-video-src="path-to-the-video.mp4"
 *   data-video-title="The title of the video. Used to track events to Google Analytics, but does not otherwise show up in this UI."
 *   data-video-poster="path-to-the-poster.jpg"
 *   data-video-width="1280"
 *   data-video-height="720"></div>
 */

import isAnimationSupported from '../../util/isAnimationSupported';
import trackEvent from '../../util/trackEvent';

const VIDEO_WRAPPER_CLASS = "content-item__video-wrapper",
    VIDEO_WRAPPER_HIDE_PLAY_BUTTON_CLASS = "content-item__video-wrapper--hide-play-button",
    BACKDROP_CLASS = "video-modal-backdrop",
    BACKDROP_HIDING_CLASS = "video-modal-backdrop--hiding",
    VIDEO_MODAL_CLASS = "video-modal",
    VIDEO_MODAL_LOADED_CLASS = "video-modal--loaded",
    VIDEO_MODAL_HIDING_CLASS = "video-modal--hiding",
    MODAL_TRIGGER_CLASS = "trigger--modal-video",
    INLINE_TRIGGER_CLASS = "trigger--inline-video",
    VIDEO_TRACKING = {};

let currentVideoEl = null,
    currentBackdrop = null,
    currentLoadingIndicator = null,
    callToActionLink = null;

// Private functions/listeners

function popstateHandler(event) {
    if (event.state && event.state.type === "video") {
        playFromUrl(event.state.data.url, Object.assign({}, event.state.data.options, { omitFromHistory: true }));
    }
    else {
        if (isModalVideoPlaying()) _hidePlayingVideo();
    }
}

function videoPlayOnClickListener (event) {
    const matches = document.body.matchesSelector || document.body.webkitMatchesSelector || document.body.mozMatchesSelector || document.body.msMatchesSelector;
    let triggerClass = null;

    if (event.target && matches.call(event.target, `.${MODAL_TRIGGER_CLASS},.${MODAL_TRIGGER_CLASS} *`)) {
        triggerClass = MODAL_TRIGGER_CLASS;
    }
    else if (event.target && matches.call(event.target, `.${INLINE_TRIGGER_CLASS},.${INLINE_TRIGGER_CLASS} *`)) {
        triggerClass = INLINE_TRIGGER_CLASS;
    }

    if (triggerClass) {
        const eventTarget = event.target.closest(`.${triggerClass}`),
            srcAttr = eventTarget.getAttribute("data-video-src"),
            videoContainer = eventTarget.parentNode,
            callToActionLink = videoContainer && videoContainer.classList.contains("multimedia-c2a--container") ? videoContainer.getElementsByTagName('a')[0] : null;

        if (srcAttr) playFromUrl(srcAttr, {
            videoTitle: eventTarget.getAttribute("data-video-title"),
            videoPoster: eventTarget.getAttribute("data-video-poster"),
            videoWidth: eventTarget.getAttribute("data-video-width") || eventTarget.querySelector("img").getAttribute("width"),
            videoHeight: eventTarget.getAttribute("data-video-height") || eventTarget.querySelector("img").getAttribute("height"),
            replaceElement: triggerClass === INLINE_TRIGGER_CLASS ? eventTarget : null,
            callToActionUrl: callToActionLink ? callToActionLink.getAttribute("href") : null,
            callToActionTarget: callToActionLink ? callToActionLink.getAttribute("target") : null,
            callToActionText: callToActionLink ? callToActionLink.textContent : null,
            omitFromHistory: triggerClass === INLINE_TRIGGER_CLASS
        });
    }
}

function videoPlaybackEventListener(event) {
    if (event.target && event.target.tagName.toLowerCase() === "video" && event.target.getAttribute("data-playback-behavior")) {
        const wrapper = event.target.closest(`.${VIDEO_WRAPPER_CLASS}`),
            callToActionLink = event.target.previousElementSibling;

        if (event.type === "ended" || event.type === "pause") {
            if (wrapper) wrapper.classList.remove(VIDEO_WRAPPER_HIDE_PLAY_BUTTON_CLASS);
            if (event.type === "ended") {
                trackEnd(event);
                if (callToActionLink) {
                    callToActionLink.classList.remove('multimedia-c2a--hide');
                    callToActionLink.classList.add('multimedia-c2a--show');
                }
            }
        }
        else if (event.type === "playing") {
            if (wrapper) wrapper.classList.add(VIDEO_WRAPPER_HIDE_PLAY_BUTTON_CLASS);
            trackPlay(event);

            if (callToActionLink) {
                callToActionLink.classList.remove('multimedia-c2a--show');
                callToActionLink.classList.add('multimedia-c2a--hide');
            }
        }
        else if (event.type === "seeking") {
            if (callToActionLink) {
                callToActionLink.classList.remove('multimedia-c2a--show');
                callToActionLink.classList.add('multimedia-c2a--hide');
            }
        }
    }
}

function videoTimeupdateListener(event) {
    const currentTime = event.target.currentTime,
        videoDuration = event.target.duration,
        percentagePlayed = currentTime / videoDuration;

    if (percentagePlayed > 0.75) { trackTimeUpdate(event.target, 75) }
    else if (percentagePlayed > 0.5) { trackTimeUpdate(event.target, 50) }
    else if (percentagePlayed > 0.25) { trackTimeUpdate(event.target, 25) }
}

function trackPlay(event) {
    const videoTitle = event.target.getAttribute("data-video-title"),
        videoSrc = event.target.getAttribute("src");

    if (videoTitle && videoSrc) {
        const videoTracking = createVideoTracking(videoTitle);
        if (!videoTracking.started) {
            videoTracking.started = true;

            trackEvent("Videos", "First Play", `${videoTitle}/${document.title}`);

            window.dataLayer = window.dataLayer || [];
            dataLayer.push({
                event: "video_play",
                videoTitle: videoTitle,
                videoUrl: videoSrc,
                pageTitle: document.title
            });
        }
    }
}

function trackEnd (event) {
    const videoTitle = event.target.getAttribute("data-video-title"),
        videoSrc = event.target.getAttribute("src");
    if (videoTitle && videoSrc) {
        const videoTracking = createVideoTracking(videoTitle);
        if (!videoTracking.ended) {
            videoTracking.ended = true;
            trackEvent("Videos", "Ended", `${videoTitle}/${document.title}`);
        }
    }
}
    
function trackTimeUpdate(target, value) {
    const videoTitle = target.getAttribute("data-video-title"),
        videoSrc = target.getAttribute("src");
    if (videoTitle && videoSrc) {
        const videoTracking = createVideoTracking(videoTitle);
        if (!videoTracking[`played${value}`]) {
            videoTracking[`played${value}`] = true;
            trackEvent("Videos", "Played", `${value}%`, `${videoTitle}/${document.title}`);
        }
    }
}

function createVideoTracking (id) {
    if (!VIDEO_TRACKING[id]) {
        VIDEO_TRACKING[id] = {
            played25: false,
            played50: false,
            played75: false,
            started: false,
            ended: false
        }
    }            
    return VIDEO_TRACKING[id];
}

function removeUrlModal () {
    if (currentVideoEl) {
        currentVideoEl.pause();
        currentVideoEl.src = "";
        currentVideoEl.parentNode.removeChild(currentVideoEl);
        currentVideoEl = null;
    }
    if (currentBackdrop) {
        currentBackdrop.parentNode.removeChild(currentBackdrop);
        currentBackdrop = null;
    }
    if (currentLoadingIndicator) {
        currentLoadingIndicator.parentNode.removeChild(currentLoadingIndicator);
        currentLoadingIndicator = null;
    }
}

function enableAutoDismiss () {
    document.addEventListener("keydown", autoDismissUrlModal);
}

function disableAutoDismiss () {
    document.removeEventListener("keydown", autoDismissUrlModal);
}

function autoDismissUrlModal (event) {
    if (event.type === "keydown" && event.keyCode === 27) hidePlayingVideo();
}

// API

/**
 * @function playFromUrl
 * @memberof Promega.Components.Video
 * @description Loads a video from the given URL and begins playback in a modal dialog.
 * @param {String} url The URL of the video to play
 * @param {JSON} [options]
 * @param {String} options.videoTitle The title of the video (used for event tracking purposes)
 * @param {String} options.videoPoster A URL to a poster image to use for the video
 * @param {Number} options.videoWidth The desired width (in pixels) at which to display the video
 * @param {Number} options.videoHeight The desired height (in pixels) at which to display the video
 * @param {HTMLElement} options.replaceElement An HTML element to replace with the loaded video, in lieu of displaying it in a modal dialog
 */
export function playFromUrl (url, options) {
    if (url && !isModalVideoPlaying()) {
        const newVideoEl = document.createElement("video");
        newVideoEl.className = VIDEO_MODAL_CLASS;
        newVideoEl.autoplay = true;
        newVideoEl.preload = "metadata";
        newVideoEl.controls = true;
        newVideoEl.setAttribute("data-playback-behavior", "url");

        if (options) {
            if (options.videoTitle) newVideoEl.setAttribute("data-video-title", options.videoTitle);
            if (options.videoPoster) newVideoEl.poster = options.videoPoster;
            if (options.videoWidth) newVideoEl.setAttribute("width", options.videoWidth);
            if (options.videoHeight) newVideoEl.setAttribute("height", options.videoHeight);
        } 

        newVideoEl.src = url;
            if (options && options.replaceElement && options.replaceElement.nodeName) {
                newVideoEl.className = "content-item";
                options.replaceElement.parentNode.insertBefore(newVideoEl, options.replaceElement);
                options.replaceElement.parentNode.removeChild(options.replaceElement);
            }
        
        
        //display in modal
        else {
            currentVideoEl = newVideoEl;
            // Create backdrop
            currentBackdrop = document.createElement("div");
            currentBackdrop.className = BACKDROP_CLASS;
            currentBackdrop.addEventListener("click", () => hidePlayingVideo());
            document.body.appendChild(currentBackdrop);

            // Create loading indicator
            currentLoadingIndicator = document.createElement("div");
            currentLoadingIndicator.className = "loading-indicator loading-indicator--visible loading-indicator--light loading-indicator--fixed-center";
            currentLoadingIndicator.style.zIndex = "10";

            const loadingInner = document.createElement("div");
            loadingInner.className = "loading-indicator__inner";
            currentLoadingIndicator.appendChild(loadingInner);

            document.body.appendChild(currentLoadingIndicator);

            //Create call to action button
            if (options.callToActionUrl) {
                callToActionLink = document.createElement("a");
                callToActionLink.setAttribute("href", options.callToActionUrl);
                callToActionLink.setAttribute("target", options.callToActionTarget);
                callToActionLink.textContent = options.callToActionText;
                callToActionLink.className = "multimedia-c2a multimedia-c2a--hide multimedia-c2a--modal button button--sol button--small";
                
                document.body.appendChild(callToActionLink);
            }

            // Append video to body
            document.body.appendChild(newVideoEl);
            enableAutoDismiss();

            // Set up the modal video to show after loading metadata
            newVideoEl.addEventListener("loadedmetadata", () => {
                newVideoEl.classList.add(VIDEO_MODAL_LOADED_CLASS);
                currentLoadingIndicator.style.display = "none";
            });
        }

        if (!(options && options.omitFromHistory)) history.pushState({ type: "video", data: { url, options } }, options.videoTitle ? options.videoTitle : "", "");
    }
    else throw "Error: no URL specified"
}

/**
 * @function hidePlayingVideo
 * @memberof Promega.Components.Video
 * @description Hides the current video modal, if there is one currently displaying
 */
export function hidePlayingVideo() {
    history.back();
}
function _hidePlayingVideo () {
    disableAutoDismiss();
    if (isAnimationSupported()) {
        currentVideoEl.classList.add(VIDEO_MODAL_HIDING_CLASS);
        currentBackdrop.classList.add(BACKDROP_HIDING_CLASS);
        currentBackdrop.addEventListener("animationend", removeUrlModal);
        if (callToActionLink) {
            callToActionLink.classList.add('multimedia-c2a--hide');
        }
    }
    else removeUrlModal();
}

/**
 * @function isModalVideoPlaying
 * @memberof Promega.Components.Video
 * @description Returns a value indicating whether or not a video modal is currently present on the page.
 * @returns {Boolean}
 */
export function isModalVideoPlaying() {
    return !(currentBackdrop === null && currentLoadingIndicator === null && currentVideoEl === null)
}

/**
 * @function getTrackingStatus
 * @memberof Promega.Components.Video
 * @description Returns an object containing the current tracking data for the video with the given title.
 * @param {String} title The title of a video that is/has been playing on the page
 */
export function getTrackingStatus(title) {
    return VIDEO_TRACKING[title];
}
    

// Init code

{
    document.addEventListener("click", videoPlayOnClickListener);
    document.addEventListener("ended", videoPlaybackEventListener, true);
    document.addEventListener("pause", videoPlaybackEventListener, true);
    document.addEventListener("playing", videoPlaybackEventListener, true);
    document.addEventListener("seeking", videoPlaybackEventListener, true);
    document.addEventListener("timeupdate", videoTimeupdateListener, true);
    window.addEventListener("popstate", popstateHandler);
}
        
        